import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

import { ReCaptchaError } from '@/errors';

interface ReCaptchaContextType {
  executeRecaptcha: () => Promise<string>;
  resetWidget: () => void;
  isLoading: boolean;
  widgetId: number | null;
  setWidgetId: (id: number) => void;
  onCallback: (token: string) => void;
  onErrorCallback: () => void;
  onExpiredCallback: () => void;
}

const ReCaptchaContext = createContext<ReCaptchaContextType | null>(null);

interface ReCaptchaProviderProps {
  children: ReactNode;
}

export const ReCaptchaProvider: React.FC<ReCaptchaProviderProps> = ({
  children,
}) => {
  const [widgetId, setWidgetId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const resolveRef = useRef<((token: string) => void) | null>(null);
  const rejectRef = useRef<((error: Error) => void) | null>(null);

  const onCallback = useCallback((token: string) => {
    if (resolveRef.current && typeof token === 'string') {
      const resolve = resolveRef.current;
      resolveRef.current = null;
      rejectRef.current = null;
      setIsLoading(false);
      resolve(token);
    }
  }, []);

  const onErrorCallback = useCallback(() => {
    if (rejectRef.current) {
      const reject = rejectRef.current;
      resolveRef.current = null;
      rejectRef.current = null;
      setIsLoading(false);
      reject(new ReCaptchaError('reCAPTCHA error occurred'));
    }
  }, []);

  const onExpiredCallback = useCallback(() => {
    if (rejectRef.current) {
      const reject = rejectRef.current;
      resolveRef.current = null;
      rejectRef.current = null;
      setIsLoading(false);
      reject(new ReCaptchaError('reCAPTCHA expired'));
    }
  }, []);

  const executeRecaptcha = useCallback(async (): Promise<string> => {
    return new Promise((resolve, reject) => {
      // Check if there's already a pending request
      if (resolveRef.current || rejectRef.current) {
        reject(new ReCaptchaError('reCAPTCHA request already in progress'));
        return;
      }

      if (widgetId === null) {
        reject(new ReCaptchaError('reCAPTCHA widget not initialized'));
        return;
      }

      if (!window.grecaptcha || !window.grecaptcha.execute) {
        reject(new ReCaptchaError('reCAPTCHA not available'));
        return;
      }

      setIsLoading(true);

      // Store resolvers
      resolveRef.current = resolve;
      rejectRef.current = reject;

      // Execute reCAPTCHA
      try {
        window.grecaptcha.execute(widgetId);
      } catch (error) {
        resolveRef.current = null;
        rejectRef.current = null;
        setIsLoading(false);
        reject(
          new ReCaptchaError(
            `reCAPTCHA execution failed: ${error.message || error}`
          )
        );
      }
    });
  }, [widgetId]);

  const resetWidget = useCallback(() => {
    if (widgetId !== null && window.grecaptcha && window.grecaptcha.reset) {
      try {
        window.grecaptcha.reset(widgetId);
      } catch (error) {
        throw new ReCaptchaError(
          `reCAPTCHA widget failed to reset: ${error.message || error}`
        );
      }
    }
  }, [widgetId]);

  const value: ReCaptchaContextType = {
    executeRecaptcha,
    resetWidget,
    isLoading,
    widgetId,
    setWidgetId,
    onCallback,
    onErrorCallback,
    onExpiredCallback,
  };

  return (
    <ReCaptchaContext.Provider value={value}>
      {children}
    </ReCaptchaContext.Provider>
  );
};

export const useReCaptcha = (): ReCaptchaContextType => {
  const context = useContext(ReCaptchaContext);
  if (!context) {
    throw new Error('useReCaptcha must be used within a ReCaptchaProvider');
  }
  return context;
};
